import "./App.css";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Provider, useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import store from "./store/store";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Slider from "./components/Slider";
import DepositWithdrawModal from "./components/Modals/DepositWithdrawModal";
import ContactModal from "./components/Modals/ContactModal";
import LoginModal from "./components/Modals/LoginModal";
import Footer from "./components/Footer";
import PageContent from "./components/PageContent";
import { useEffect, useRef, useState } from "react";
import {
  getWebsiteSetting,
  getUsersByID,
  getPlayerBalance,
  getPlayerCash,
} from "./store/slices/user";
import useAuth from "./hooks/useAuth";
import { Icon } from "@iconify/react";
import SocketProvider from "./SocketContext";
import socketManager from "./utils/socket";
import useAutoWithdrawRuntime from "./hooks/useAutoWithdrawRuntime";

function AppLayout() {
  const location = useLocation();
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [sidebarTopOffset, setSidebarTopOffset] = useState(0);
  // Socket notification states
  const [open2, setOpen2] = useState(false);
  const [openSuccessDeposit, setOpenSuccessDeposit] = useState(false);
  const [lastPong, setLastPong] = useState(null);
  const [lastPong2, setLastPong2] = useState(null);
  const sidebarRef = useRef(null);
  const topBannerRef = useRef(null);
  const desktopMarqueeRef = useRef(null);
  const dispatch = useDispatch();
  const singleUsers = useSelector((state) => state.user.singleUsers);
  const playerBalance = useSelector((state) => state.user.playerBalance);
  const websiteSetting = useSelector((state) => state.user.websiteSetting);
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const currentUser = Array.isArray(singleUsers)
    ? singleUsers[0]
    : singleUsers?.data?.[0];
  const withdrawConditionDetail = currentUser?.withdrawConditionDetail;
  const promotionDetail = currentUser?.PromotionDetail;
  const promotionStatus = currentUser?.PromotionStatus;
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  const backgroundPath = Array.isArray(websiteSetting)
    ? websiteSetting[0]?.webBackgroundUrl
    : websiteSetting?.data?.[0]?.webBackgroundUrl ||
      websiteSetting?.webBackgroundUrl;
  const backgroundUrl = backgroundPath
    ? backgroundPath.startsWith("http")
      ? backgroundPath
      : `${apiBaseUrl}${
          backgroundPath.startsWith("/") ? "" : "/"
        }${backgroundPath}`
    : "";
  const hideSidebar = location.pathname === "/menu";
  const isMenuPage = location.pathname === "/menu";
  const isFullWidthBannerRoute = [
    "/slot",
    "/livecasino",
    "/sportbook",
    "/card",
  ].includes(location.pathname);

  const { autoWithdrawAlert, closeAutoWithdrawAlert } = useAutoWithdrawRuntime({
    dispatch,
    isAuthenticated,
    user,
    userId,
    playerBalance,
  });

  // rework renderTurnPromo with mobile-equal margins
  const renderTurnPromo = () => {
    const turnBg = "#ff800033"; // blue for turn notice
    const isPromoActive = promotionStatus === "ACTIVE";
    const promoBg = isPromoActive ? "#ff800033" : "#555555"; // dark orange when received, grey when not received
    const commonBox = {
      borderRadius: 8,
      padding: "12px 16px",
      margin: isMenuPage ? "16px auto" : "16px 16px",
      width: isMenuPage ? "min(920px, calc(100% - 32px))" : "auto",
      maxWidth: isMenuPage ? "920px" : "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: 12,
    };
    return (
      <>
        {withdrawConditionDetail && (
          <div style={{ ...commonBox, background: turnBg }}>
            <Icon
              icon="fluent-color:error-circle-48"
              width={40}
              height={40}
              style={{ borderRadius: 8 }}
            />
            <span
              style={{
                color: "#fff",
                fontSize: "clamp(12px, 2.8vw, 16px)",
                lineHeight: 1.35,
              }}
            >{`คุณติดเทิร์น - ${withdrawConditionDetail}`}</span>
          </div>
        )}

        {promotionDetail && (
          <div style={{ ...commonBox, background: promoBg }}>
            <Icon
              icon="fluent-color:megaphone-loud-32"
              width={40}
              height={40}
              style={{
                borderRadius: 8,
                filter: isPromoActive ? "none" : "grayscale(1)",
              }}
            />
            <span style={{ color: "#fff" }}>
              {isPromoActive ? (
                `คุณอยู่ระหว่างรับโปร - ${promotionDetail}`
              ) : (
                <>
                  {`คุณได้เลือกโปร - ${promotionDetail}`}
                  <br />
                  <span style={{ fontSize: 12, opacity: 0.85 }}>
                    กรุณาฝากเงินเพื่อรับโปร
                  </span>
                </>
              )}
            </span>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    dispatch(getWebsiteSetting());
  }, [dispatch]);

  useEffect(() => {
    const body = document.body;
    if (backgroundUrl) {
      body.style.setProperty("--app-bg-image", `url("${backgroundUrl}")`);
    } else {
      body.style.setProperty("--app-bg-image", "none");
    }
  }, [backgroundUrl]);

  useEffect(() => {
    if (!isFullWidthBannerRoute || hideSidebar) {
      setSidebarTopOffset(0);
      return;
    }

    let rafId = 0;

    const updateSidebarOffset = () => {
      const desktopMarqueeEl = desktopMarqueeRef.current;
      const topBannerEl = topBannerRef.current;
      if (!desktopMarqueeEl && !topBannerEl) {
        setSidebarTopOffset(0);
        return;
      }

      const marqueeBottom = desktopMarqueeEl
        ? desktopMarqueeEl.getBoundingClientRect().bottom
        : 0;
      const bannerBottom = topBannerEl
        ? topBannerEl.getBoundingClientRect().bottom
        : 0;
      const bottom = Math.max(marqueeBottom, bannerBottom);

      setSidebarTopOffset(Math.max(0, Math.ceil(bottom + window.scrollY + 8)));
    };

    const scheduleOffsetUpdate = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateSidebarOffset);
    };

    scheduleOffsetUpdate();
    const delayIds = [120, 320, 650, 1000].map((delay) =>
      window.setTimeout(scheduleOffsetUpdate, delay),
    );

    window.addEventListener("resize", scheduleOffsetUpdate);
    window.addEventListener("load", scheduleOffsetUpdate);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleOffsetUpdate();
          })
        : null;

    if (ro && topBannerRef.current) ro.observe(topBannerRef.current);
    if (ro && desktopMarqueeRef.current) ro.observe(desktopMarqueeRef.current);

    const observedImages = [];
    [topBannerRef.current, desktopMarqueeRef.current].forEach((rootEl) => {
      if (!rootEl) return;
      rootEl.querySelectorAll("img").forEach((img) => {
        if (img.complete) return;
        const onLoad = () => scheduleOffsetUpdate();
        img.addEventListener("load", onLoad);
        img.addEventListener("error", onLoad);
        observedImages.push({ img, onLoad });
      });
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      delayIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", scheduleOffsetUpdate);
      window.removeEventListener("load", scheduleOffsetUpdate);
      if (ro) ro.disconnect();
      observedImages.forEach(({ img, onLoad }) => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onLoad);
      });
    };
  }, [
    hideSidebar,
    isFullWidthBannerRoute,
    location.pathname,
    promotionDetail,
    withdrawConditionDetail,
  ]);

  // fetch latest singleUsers by current user id
  useEffect(() => {
    if (userId) {
      dispatch(getUsersByID(userId));
    }
  }, [userId, dispatch]);

  // Replace previous socket effect with instance-based binding
  useEffect(() => {
    const socket = socketManager;

    if (!socket || !user || !user.id) return;

    const baseSocket = socket.connect();
    const userChannel = String(user.id);
    const depositName = `deposit-${user.id}`;
    const depositPaymentName = `depositpayment-${user.id}`;

    try {
      baseSocket.emit("join-user-room", user.id);
    } catch {}

    const onNotApprove = (res) => {
      setLastPong(`ไม่อนุมัติถอนเงิน! ${res}`);
      setOpen2(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        setOpen2(false);
      }, 6000);
    };

    const onDeposit = (res) => {
      setLastPong2(`ฝากเงินจำนวน ${res} บาท สำเร็จ!`);
      setOpenSuccessDeposit(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        dispatch(getUsersByID(user));
        dispatch(getPlayerCash(user));
        setOpenSuccessDeposit(false);
      }, 6000);
    };

    const onDepositPayment = (res) => {
      console.log("Received event:", depositPaymentName, "with data:", res);
      setLastPong2(`ฝากเงินจำนวน ${res} บาท สำเร็จ!`);
      setOpenSuccessDeposit(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        dispatch(getUsersByID(user));
        dispatch(getPlayerCash(user));
        setOpenSuccessDeposit(false);

        const depositModal = document.querySelector(".deposit-modal");
        if (depositModal) {
          const depositComponent =
            depositModal.__reactFiber$?.return?.stateNode;
          if (depositComponent?.handleClose) {
            depositComponent.handleClose();
          }
        }
      }, 6000);
    };

    baseSocket.on(userChannel, onNotApprove);
    baseSocket.on(depositName, onDeposit);
    baseSocket.on(depositPaymentName, onDepositPayment);

    return () => {
      try {
        baseSocket.off(userChannel, onNotApprove);
        baseSocket.off(depositName, onDeposit);
        baseSocket.off(depositPaymentName, onDepositPayment);
      } catch (e) {
        // ignore
      }
    };
  }, [dispatch, user]);

  // Listen for global user updates (fallback)
  useEffect(() => {
    const onUserUpdate = (e) => {
      if (e?.detail?.userId && e.detail.userId === userId) {
        dispatch(getUsersByID(userId));
      }
    };
    window.addEventListener("app:userUpdate", onUserUpdate);
    return () => window.removeEventListener("app:userUpdate", onUserUpdate);
  }, [dispatch, userId]);

  const isLoginPage = location.pathname === "/login";

  if (isLoginPage) {
    return (
      <div className="App">
        <PageContent />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="App-header-bg sticky-header">
        <Header
          onLoginClick={() => setIsLoginModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>

      {/* Custom Socket Alerts (no MUI) */}
      {open2 && (
        <div className="app-alert-wrapper" style={{ top: 80 }}>
          <div className="app-alert app-alert--warning">
            <Icon icon="mdi:alert" width={22} height={22} />
            <span style={{ color: "#000" }}>{lastPong}</span>
            <button
              className="app-alert__close"
              aria-label="close"
              onClick={() => setOpen2(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {openSuccessDeposit && (
        <div className="app-alert-wrapper" style={{ top: 120 }}>
          <div className="app-alert app-alert--success">
            <Icon icon="mdi:check-circle" width={22} height={22} />
            <span style={{ color: "#000" }}>{lastPong2}</span>
            <button
              className="app-alert__close"
              aria-label="close"
              onClick={() => setOpenSuccessDeposit(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {autoWithdrawAlert?.open && (
        <div className="app-alert-wrapper" style={{ top: 160 }}>
          <div
            className={`app-alert ${
              autoWithdrawAlert?.type === "success"
                ? "app-alert--success"
                : "app-alert--warning"
            }`}
          >
            <Icon
              icon={
                autoWithdrawAlert?.type === "success"
                  ? "mdi:check-circle"
                  : "mdi:alert-circle"
              }
              width={22}
              height={22}
            />
            <span style={{ color: "#000" }}>{autoWithdrawAlert?.text}</span>
            <button
              className="app-alert__close"
              aria-label="close"
              onClick={closeAutoWithdrawAlert}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="App-content">
        {!hideSidebar && (
          <div ref={sidebarRef} className="desktop-sidebar">
            <Sidebar
              isMobileMenuOpen={false}
              onMobileMenuToggle={() => {}}
              offsetTop={isFullWidthBannerRoute ? sidebarTopOffset : 0}
            />
          </div>
        )}
        <main
          className={`App-main${location.pathname === "/menu" ? " App-main--no-margin" : ""}${isFullWidthBannerRoute ? " App-main--fullwidth-banner" : ""}`}
        >
          {!hideSidebar && (
            <div ref={topBannerRef} className="app-top-banner">
              <Slider />
            </div>
          )}
          <div className="mobile-marquee">
            {!isMenuPage ? renderTurnPromo() : null}
          </div>
          <div className="mobile-content">
            {!hideSidebar && (
              <div ref={sidebarRef} className="mobile-sidebar">
                <Sidebar
                  isMobileMenuOpen={false}
                  onMobileMenuToggle={() => {}}
                />
              </div>
            )}
            <div className="mobile-right-content">
              <div className="mobile-page-content">
                <PageContent />
              </div>
            </div>
          </div>
          {/* Turn and Promotion Details - Desktop only */}
          <div ref={desktopMarqueeRef} className="desktop-marquee">
            {renderTurnPromo()}
          </div>

          <div className="desktop-page-content">
            <PageContent />
          </div>
        </main>
      </div>
      {!isMenuPage && (
        <Footer
          onDepositClick={() => setIsDepositModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      )}
      {/* Modals */}
      <DepositWithdrawModal
        isOpen={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <SocketProvider>
        <Router>
          <AppLayout />
        </Router>
      </SocketProvider>
    </Provider>
  );
}

export default App;
